import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";

import {
  ProjectListContainer,
  ProjectListViewContainer,
  ProjectSection,
} from "./component/common/styled/project";

import SideMenu from "./component/common/component/sideMenu";
import Header from "./component/common/component/header";
import TooltipSideMenu from "../../../common/tooltipSideMenu";
import Projects from "./component/projectListPage/projects";
import Info from "./component/projectListPage/info";
import { useDisableScroll } from "../../../hooks/useDisableScroll";

const ProjectList = () => {
  const user = useSelector((state) => state?.user?.data[0]);
  const company = useSelector((state) => state?.company?.data);

  const [sideMenuState, setSideMenuState] = useState({
    show: false,
    clicked: false,
  });
  const [projects, setProjects] = useState({});
  const [originProjects, setOriginProjects] = useState({});
  const [isDoneProjects, setIsDoneProjects] = useState(false);
  const [selectCompany, setSelectCompany] = useState();

  const [searched, setSearched] = useState("");

  useEffect(() => {
    if (user) {
      getProjectList();
    }
  }, [user]);

  useDisableScroll();

  const filterProjectsByCompany = (projectList, targetOrgNo) => {
    return {
      target: projectList.filter((item) => item.orgNo.toString() === targetOrgNo.toString()),
      others: projectList.filter((item) => item.orgNo.toString() !== targetOrgNo.toString()),
    };
  };

  const excludeCompanyProjects = (projectList, companyList) => {
    return projectList.filter((item) => !companyList.some((cmpny) => cmpny.cmpnyNo === item.orgNo));
  };

  const hasProjectAccess = (project) => {
    return project.pinnedYn !== "Y" && (project.publicYn !== "N" || project.projectParticipantNo);
  };

  const getProjectList = async (completeYn = "N") => {
    if (!user) return;

    try {
      const url = "/api/projectList";
      const body = {
        mberNo: user.mberNo,
        completeYn,
        delYn: "N",
      };

      const res = await axios.post(url, body);
      if (res.status === 200) {
        const data = res.data;

        if (Array.isArray(company) && company.length > 0) {
          setSelectCompany({ ...company[0] });

          const cmpnyList = await getCompanyProjectList(company[0].cmpnyNo, completeYn);
          const { target, others } = filterProjectsByCompany(cmpnyList, company[0].cmpnyNo);

          data.cmpnyProjectList = target;
          data.otherProjectList = others;
        }

        data.otherProjectList = excludeCompanyProjects(data.otherProjectList || [], company || []);

        setProjects(data);
        setOriginProjects(data);
      }
    } catch (error) {
      console.error("프로젝트 목록 조회 실패", error);
      toast.error("프로젝트 목록을 불러오는데 실패했습니다.");
    }
  };

  const getCompanyProjectList = async (orgNo, completeYn) => {
    if (!orgNo || !user) return [];

    try {
      const url = "/api/projectList";
      const body = {
        mberNo: user.mberNo,
        gbn: "C",
        orgNo,
        completeYn,
        delYn: "N",
      };

      const res = await axios.post(url, body);
      if (res.status === 200) {
        const data = res?.data?.fixedProjectList || [];
        return data.filter(hasProjectAccess);
      }
    } catch (error) {
      console.error("회사 프로젝트 목록 조회 실패", error);
    }

    return [];
  };

  const handleSearch = () => {
    if (!originProjects) return;

    const filtered = {};
    const searchTerm = searched.toLowerCase();

    for (const key in originProjects) {
      if (originProjects.hasOwnProperty(key)) {
        const projectList = originProjects[key];
        if (Array.isArray(projectList)) {
          filtered[key] = projectList.filter((project) =>
            project.projectNm.toLowerCase().includes(searchTerm),
          );
        }
      }
    }

    setProjects(filtered);
  };

  const projectPinned = async (e, project) => {
    e.stopPropagation();

    if (!user) return;

    const isCurrentlyPinned = project.pinnedYn === "Y";

    if (project.completeYn === "Y") {
      toast.error("해당 프로젝트는 변경할 수 없습니다.");
      return;
    }

    try {
      const url = "/api/participants";
      const body = {
        projectParticipantNo: project.projectParticipantNo,
        projectNo: project.projectNo,
        gbn: project.gbn,
        mberNo: user.mberNo,
        orgNo: project.gbn === "C" ? selectCompany?.cmpnyNo : project.orgNo,
        partiType: project.partiType,
        pinnedYn: isCurrentlyPinned ? "N" : "Y",
        delYn: "N",
        updateMberId: user.userName,
      };

      const res = await axios.put(url, [body]);

      if (res.status === 200) {
        await getProjectList(isDoneProjects ? null : "N");
        toast.success(`즐겨찾기가 ${isCurrentlyPinned ? "취소" : "등록"}되었습니다.`);
      }
    } catch (error) {
      console.error("즐겨찾기 처리 실패", error);
      toast.error("즐겨찾기 처리에 실패했습니다.");
    }
  };

  const handleDoneProject = (e) => {
    const { checked } = e.target;

    setIsDoneProjects(checked);
    getProjectList(checked ? null : "N");
  };

  const changeCompany = async (e) => {
    const { value } = e.target;

    if (!company) return;

    const target = company.find((item) => item.cmpnyNo.toString() === value.toString());
    if (!target) return;

    setSelectCompany({ ...target });

    try {
      const cmpnyList = await getCompanyProjectList(value, isDoneProjects ? null : "N");
      const { target: targetProjects, others } = filterProjectsByCompany(cmpnyList, value);

      const projectList = {
        ...originProjects,
        cmpnyProjectList: targetProjects,
        otherProjectList: others,
      };

      setOriginProjects(projectList);
      setProjects(projectList);
    } catch (error) {
      console.error("회사 변경 실패", error);
      toast.error("회사 변경에 실패했습니다.");
    }
  };

  return (
    <ProjectSection>
      <ProjectListContainer>
        <Header
          clickSideMenu={sideMenuState.clicked}
          setClickSideMenu={(clicked) => setSideMenuState((prev) => ({ ...prev, clicked }))}
        />
        <div className="inner">
          <SideMenu
            showSideMenu={sideMenuState.show}
            setShowSideMenu={(show) => setSideMenuState((prev) => ({ ...prev, show }))}
          />
          <ProjectListViewContainer>
            <Info
              isDoneProjects={isDoneProjects}
              handleDoneProject={handleDoneProject}
              searched={searched}
              setSearched={setSearched}
              setShowSideMenu={(show) => setSideMenuState((prev) => ({ ...prev, show }))}
              showSideMenu={sideMenuState.show}
              clickSideMenu={sideMenuState.clicked}
              setClickSideMenu={(clicked) => setSideMenuState((prev) => ({ ...prev, clicked }))}
              handleSearch={handleSearch}
            />
            {Object.keys(projects).map((gbn, index) => (
              <Projects
                key={`${gbn}-${index}`}
                gbn={gbn}
                changeCompany={changeCompany}
                selectCompany={selectCompany}
                projects={projects}
                projectPinned={projectPinned}
              />
            ))}
          </ProjectListViewContainer>
        </div>
        {sideMenuState.clicked && (
          <TooltipSideMenu
            clickSideMenu={sideMenuState.clicked}
            setClickSideMenu={(clicked) => setSideMenuState((prev) => ({ ...prev, clicked }))}
          />
        )}
      </ProjectListContainer>
    </ProjectSection>
  );
};

export default ProjectList;
